import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker";
import { Helmet } from "react-helmet-async";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import FormatTime from "../../AdsPage/FormatTime";
import { fonts, headingFonts, notFoundBox } from "../../../Styles/Styles";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); // Hook to navigate

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/notifications/all`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = await response.json();
        setNotifications(data);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);
  console.log(notifications);

  const toggleReadStatus = async (notificationId, isRead) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/notifications/mark-read/${notificationId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ read: !isRead }),
        }
      );
      if (response.ok) {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) =>
            notification._id === notificationId
              ? { ...notification, read: !isRead }
              : notification
          )
        );
      } else {
        console.error("Failed to update notification status");
      }
    } catch (error) {
      console.error("Error toggling read status:", error);
    }
  };

  const handleNotificationClick = async (notificationId, adId) => {
    // Mark the notification as read when the user clicks on it
    toggleReadStatus(notificationId, false); // Assuming you want to mark it as read
    // Once marked as read, navigate to the ad details page
    navigate(`/ad/${adId}`);
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Notifications - Listed.ky</title>
        <meta name="description" content="Notifications - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        Notifications
      </Typography>
      {notifications.length > 0 ? (
        <List>
          {notifications.map((notification) => (
            <React.Fragment key={notification._id}>
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault(); // Prevent default link behavior
                  handleNotificationClick(notification._id, notification.adId); // Mark as read and navigate
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <ListItem
                  button
                  sx={{
                    backgroundColor: notification.read
                      ? "rgba(0, 0, 0, 0.04)"
                      : "rgba(255, 0, 0, 0.1)",
                    "&:hover": {
                      backgroundColor: notification.read
                        ? "rgba(0, 0, 0, 0.08)"
                        : "rgba(255, 0, 0, 0.2)",
                    },
                    position: "relative",
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ ...fonts }}>
                        {notification.message}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ ...fonts }}>
                        <FormatTime createdAt={notification.createdAt} />
                      </Typography>
                    }
                  />

                  {/* Mark as read/unread icon */}
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent this click event from triggering Link
                      e.preventDefault(); // Prevent link navigation on click
                      toggleReadStatus(notification._id, notification.read);
                    }}
                  >
                    {notification.read ? (
                      <MarkEmailUnreadIcon />
                    ) : (
                      <MarkEmailReadIcon />
                    )}
                  </IconButton>
                </ListItem>
              </Link>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Box sx={{ ...notFoundBox }}>
          <SearchOffOutlinedIcon
            sx={{
              color: "var(--primary-color)",
              fontSize: 50,
              opacity: 0.5,
              marginBottom: 1,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "var(--primary-color)",
              ...headingFonts,
            }}
          >
            No notifications found.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, ...fonts }}>
            Check back Later.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Notifications;
